/*!

=========================================================
* Argon Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
import { useSelector } from 'react-redux'
import firebase from 'firebase';
import { loadStripe } from '@stripe/stripe-js';
// react component used to create sweet alerts
import ReactBSAlert from "react-bootstrap-sweetalert";

// reactstrap components
import {
  Badge,
  Card,
  CardHeader,
  Container,
  Row,
  Table,
  Button,
  CardBody,
  CardFooter,
  Col,
} from "reactstrap";
// core components

import BillingHeader from "components/Headers/BillingHeader.js";
import AuthHeader from 'components/Headers/AuthHeader';
import Loading from "components/Misc/Loading";

function Billing() {
  const userState = useSelector(state => state.user);
  const [isLoading, setIsLoading] = React.useState(false);
  const [alert, setAlert] = React.useState(false);

  const handlePayment = async (product) => {
    try {
      setIsLoading(true);
      const docRef = await firebase
        .firestore()
        .collection('customers')
        .doc(userState.user.uid)
        .collection('checkout_sessions')
        .add({
          price: product,
          success_url: `${window.location.origin}/admin/dashboard?success=true`,
          cancel_url: `${window.location.origin}/admin/dashboard?success=false`,
        })

      docRef.onSnapshot(async snap => {
        try {
          if(snap.data().sessionId) {
            const stripe = await loadStripe('pk_test_51Isqv2LOSG9MtVLrhmATMKGtAeweeDDSsSYJR8idCmko0JtGl8IxCgzy4eE4bJ1ZTFhxWBSdP0PvgcIoNBu6hfcT00k3EROy3f');
            console.log(stripe);
            await stripe.redirectToCheckout({ sessionId: snap.data().sessionId });
          }
        } catch(error) {
          throw new Error (`Error on stripe document creation: ${error}`)
        }
      })
    } catch(error) {
      console.log(error)
    }
  }

  const handleManageAccount = async () => {
    try {
      setIsLoading(true);
      const functionRef = firebase.default.functions().httpsCallable('ext-firestore-stripe-subscriptions-createPortalLink');
      const { data } = await functionRef({returnUrl: window.location.origin})
      window.location.assign(data.url);
    } catch(error) {
      console.log(error);
    }
  }

  if (isLoading) {
    return (
      <Loading active={true} />
    )
  }

  if (userState.user.role === 'owner') {
    return (
      <>
        {alert}
        <AuthHeader title="Choose your plan" lead="" />
        <Container className="mt--8 pb-5 bg-default" style={{minWidth: '100%'}}>
          <Row className="justify-content-center">
            <Col lg="10">
              <div className="pricing card-group flex-column flex-xl-row  mb-3 flex-nowrap">
                <Card className="card-pricing border-0 text-center mb-4" style={{minWidth: '300px'}}  style={{minWidth: '300px'}}>
                  <CardHeader className="bg-transparent">
                    <h4 className="text-uppercase ls-1 text-info py-3 mb-0">
                      Free Plan
                    </h4>
                  </CardHeader>
                  <CardBody className="px-lg-7">
                    <div className="display-2">$0</div>
                    <ul className="list-unstyled my-4">
                      <li>
                        <div className="d-flex align-items-center">
                          <div>
                            <div className="icon icon-xs icon-shape bg-gradient-info shadow rounded-circle text-white">
                              <i className="ni ni-bullet-list-67" />
                            </div>
                          </div>
                          <div>
                            <span className="pl-2">1 Checklist</span>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="d-flex align-items-center">
                          <div>
                            <div className="icon icon-xs icon-shape bg-gradient-info shadow rounded-circle text-white">
                              <i className="far fa-chart-bar" />
                            </div>
                          </div>
                          <div>
                            <span className="pl-2">
                              Performance Analytics
                            </span>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="d-flex align-items-center">
                          <div>
                            <div className="icon icon-xs icon-shape bg-gradient-info shadow rounded-circle text-white">
                              <i className="fas fa-mobile-alt" />
                            </div>
                          </div>
                          <div>
                            <span className="pl-2">1 Mobile App account</span>
                          </div>
                        </div>
                      </li>
                    </ul>
                    { 
                      userState.user.plan !== 'free' ? 
                        <Button className="mb-3" color="info" type="button" onClick={() => {
                          setAlert(
                            <ReactBSAlert
                              warning
                              showCancel
                              style={{ display: "block", marginTop: "-100px" }}
                              title="Warning!"
                              onConfirm={handleManageAccount}
                              onCancel={() => setAlert(null)}
                              confirmBtnBsStyle="primary"
                              confirmBtnText="I still want to cancel"
                              cancelBtnBsStyle="secondary"
                              cancelBtnText="Cancel"
                              btnSize=""
                            >
                              After you cancel your account, you will have 90 days to keep using your checklists as you have so far, without being able to add any more or add any more mobile app accounts. After 90 days both, the checklists and the mobile app accesses will be permanently deleted.
                            </ReactBSAlert>
                          )
                        }}>
                          Downgrade
                        </Button>
                        :
                        null
                    }
                  </CardBody>
                  {
                    userState.user.plan === 'free' ?
                      <CardFooter className="bg-transparent">
                        <h4>Current Plan</h4>
                      </CardFooter>
                    :
                      null
                  }
                </Card>
                <Card className="card-pricing bg-gradient-success zoom-in shadow-lg rounded border-0 text-center mb-4"  style={{minWidth: '300px'}}>
                  <CardHeader className="bg-transparent">
                    <h4 className="text-uppercase ls-1 text-white py-3 mb-0">
                      Pro Yearly
                    </h4>
                  </CardHeader>
                  <CardBody className="px-lg-7">
                    <div className="display-2 text-white d-flex">$359.<small className="display-4">99</small></div>
                    <span className="text-white">$29.99/month billed annually</span>
                    <ul className="list-unstyled my-4">
                      <li>
                        <div className="d-flex align-items-center text-white">
                          <div>
                            <div className="icon icon-xs icon-shape bg-gradient-info shadow rounded-circle text-white">
                              <i className="ni ni-bullet-list-67" />
                            </div>
                          </div>
                          <div>
                            <span className="pl-2">Unlimited Checklists</span>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="d-flex align-items-center text-white">
                          <div>
                            <div className="icon icon-xs icon-shape bg-gradient-info shadow rounded-circle text-white">
                              <i className="far fa-chart-bar" />
                            </div>
                          </div>
                          <div>
                            <span className="pl-2">
                              Performance Analytics
                            </span>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="d-flex align-items-center text-white">
                          <div>
                            <div className="icon icon-xs icon-shape bg-gradient-info shadow rounded-circle text-white">
                              <i className="far fa-calendar" />
                            </div>
                          </div>
                          <div>
                            <span className="pl-2">
                              Per-day Analytics
                            </span>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="d-flex align-items-center text-white">
                          <div>
                            <div className="icon icon-xs icon-shape bg-gradient-info shadow rounded-circle text-white">
                              <i className="far fa-grin-hearts" />
                            </div>
                          </div>
                          <div>
                            <span className="pl-2">
                              Customer Feedback Report
                            </span>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="d-flex align-items-center text-white">
                          <div>
                            <div className="icon icon-xs icon-shape bg-gradient-info shadow rounded-circle text-white">
                              <i className="fas fa-mobile-alt" />
                            </div>
                          </div>
                          <div>
                            <span className="pl-2">Unlimited Mobile App accounts</span>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="d-flex align-items-center text-white">
                          <div>
                            <div className="icon icon-xs icon-shape bg-gradient-info shadow rounded-circle text-white">
                              <i className="fas fa-user" />
                            </div>
                          </div>
                          <div>
                            <span className="pl-2">Per-Manager Details</span>
                          </div>
                        </div>
                      </li>
                    </ul>
                    { 
                      userState.user.plan !== 'year' ? 
                        <Button className="mb-3" color="info" type="button" onClick={() => userState.user.plan === 'month' ? handleManageAccount() : handlePayment('price_1IstujLOSG9MtVLrpfA2jDEY')}>
                          Upgrade
                        </Button>
                        :
                        null
                    }
                  </CardBody>
                  {
                    userState.user.plan === 'year' ?
                      <CardFooter className="bg-transparent">
                        <h4>Current Plan</h4>
                      </CardFooter>
                    :
                      null
                  }
                </Card>
                <Card className="card-pricing border-0 text-center mb-4"  style={{minWidth: '300px'}}>
                  <CardHeader className="bg-transparent">
                    <h4 className="text-uppercase ls-1 text-info py-3 mb-0">
                      Pro Monthly
                    </h4>
                  </CardHeader>
                  <CardBody className="px-lg-7">
                  <div className="display-2 d-flex">$39.<small className="display-4">99</small></div>
                    <span>billed monthly $479.88/year</span>
                    <ul className="list-unstyled my-4">
                      <li>
                        <div className="d-flex align-items-center">
                          <div>
                            <div className="icon icon-xs icon-shape bg-gradient-info shadow rounded-circle text-white">
                              <i className="ni ni-bullet-list-67" />
                            </div>
                          </div>
                          <div>
                            <span className="pl-2">Unlimited Checklists</span>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="d-flex align-items-center">
                          <div>
                            <div className="icon icon-xs icon-shape bg-gradient-info shadow rounded-circle text-white">
                              <i className="far fa-chart-bar" />
                            </div>
                          </div>
                          <div>
                            <span className="pl-2">
                              Performance Analytics
                            </span>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="d-flex align-items-center">
                          <div>
                            <div className="icon icon-xs icon-shape bg-gradient-info shadow rounded-circle text-white">
                              <i className="far fa-calendar" />
                            </div>
                          </div>
                          <div>
                            <span className="pl-2">
                              Per-day Analytics
                            </span>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="d-flex align-items-center">
                          <div>
                            <div className="icon icon-xs icon-shape bg-gradient-info shadow rounded-circle text-white">
                              <i className="far fa-grin-hearts" />
                            </div>
                          </div>
                          <div>
                            <span className="pl-2">
                              Customer Feedback Report
                            </span>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="d-flex align-items-center">
                          <div>
                            <div className="icon icon-xs icon-shape bg-gradient-info shadow rounded-circle text-white">
                              <i className="fas fa-mobile-alt" />
                            </div>
                          </div>
                          <div>
                            <span className="pl-2">Unlimited Mobile App accounts</span>
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="d-flex align-items-center">
                          <div>
                            <div className="icon icon-xs icon-shape bg-gradient-info shadow rounded-circle text-white">
                              <i className="fas fa-user" />
                            </div>
                          </div>
                          <div>
                            <span className="pl-2">Per-Manager Details</span>
                          </div>
                        </div>
                      </li>
                    </ul>
                    { 
                      userState.user.plan !== 'month' ? 
                        <Button className="mb-3" color="info" type="button" onClick={() => userState.user.plan === 'free' ? handlePayment('price_1ItHDbLOSG9MtVLrAd9tLMpn') : handleManageAccount()}>
                          { userState.user.plan === 'free' ? 'Upgrade' : 'Downgrade'}
                        </Button>
                        :
                        null
                    }
                  </CardBody>
                  {
                    userState.user.plan === 'month' ?
                      <CardFooter className="bg-transparent">
                        <h4>Current Plan</h4>
                      </CardFooter>
                    :
                      null
                  }
                </Card>
              </div>
            </Col>
          </Row>
          <div className="d-flex px-3 mt-5 ml-5">
            <Col lg="6">
            <a
              className="text-light"
              href="#manage-account"
              onClick={handleManageAccount}
            >
              Manage Account
            </a>
            </Col>
          </div>
        </Container>
      </>
    )
  }
  return (
    <>
      <BillingHeader name="Billing" parentName="Pages" />
      <Container className="mt--6" fluid>
        <Row>
        <div className="col">
            <Card className="bg-default shadow">
              <CardHeader className="bg-transparent border-0">
                <h3 className="text-white mb-0">Billing</h3>
              </CardHeader>
              <Table
                className="align-items-center table-dark table-flush"
                responsive
              >
                <thead className="thead-dark">
                  <tr>
                    <th className="sort" data-sort="location" scope="col">
                      Location
                    </th>
                    <th className="sort" data-sort="plan" scope="col">
                      Plan
                    </th>
                    <th className="sort" data-sort="paymentDate" scope="col">
                      Last Pay
                    </th>
                    <th className="sort" data-sort="expirationDate" scope="col">
                      Expiring in
                    </th>
                  </tr>
                </thead>
                <tbody className="list">
                  <tr>
                    <th scope="row">
                      <span className="location mb-0 text-sm">
                        Tacos4Life
                      </span>
                    </th>
                    <td className="plan">Basic</td>
                    <td>
                      <Badge color="" className="badge-dot mr-4">
                        <span className="status">Jan/15/2021</span>
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <span className="completion mr-2">Jan/15/2022</span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">
                      <span className="location mb-0 text-sm">
                        McDonald's
                      </span>
                    </th>
                    <td className="plan">Pro</td>
                    <td>
                      <Badge color="" className="badge-dot mr-4">
                        <span className="status">Jul/05/2021</span>
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <span className="completion mr-2">Jul/05/2021</span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">
                      <span className="location mb-0 text-sm">
                        Burger King
                      </span>
                    </th>
                    <td className="plan">Basic</td>
                    <td>
                      <Badge color="" className="badge-dot mr-4">
                        <span className="status">Feb/28/2021</span>
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <span className="completion mr-2">Mar/28/2021</span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">
                      <span className="location mb-0 text-sm">
                        Legion Brewing
                      </span>
                    </th>
                    <td className="plan">Basic</td>
                    <td>
                      <Badge color="" className="badge-dot mr-4">
                        <span className="status">Dec/30/2020</span>
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <span className="completion mr-2">EXPIRED</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card>
            </div>
        </Row>
      </Container>
    </>
  );
}

export default Billing;
